import moment from 'moment-timezone';
import Moment = moment.Moment;
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { BaseAvatar } from './base/avatar';
import { SimpleOrganization } from '../../services/organization/organization.service';

/**
 * SD-9097: how many organizations a rep actually holds, read from whichever queue they are in.
 *
 * `organizationsCount` counts ONLY onboarding assignments (`organizations.assigned_sales_rep_id`),
 * so for a BDR it is always 0. Every consumer that asks "does this rep hold anything" must go
 * through here — reading the raw count sent BDRs down the "holds nothing" path, which both deleted
 * them without reassigning (stranding their orgs) and permanently disabled their Lead Assignments
 * menu item.
 *
 * A free function rather than a getter on the class on purpose: this area's specs build reps as
 * type-cast object literals, not real instances, so a getter would be `undefined` in every one.
 */
export function organizationsHeldBy(rep: AssignedSalesRep): number {
    return (rep.isBdr ? rep.bdrOrganizationsCount : rep.organizationsCount) || 0;
}

export class AssignedSalesRep extends BaseAvatar {
    id: number;

    /** The rep's USER id — what organizations.assigned_sales_rep_id / assigned_bdr_rep_id store. */
    userId: number;

    email: string;
    phone: string;
    meetingLink: string;
    secondaryMeetingLink: string;

    lastLead: Moment;
    excludeFromLeads: boolean;
    isBdr: boolean;
    weight: number;
    skipStart: Moment;
    skipEnd: Moment;
    lastSkipped: Moment;
    organizations: SimpleOrganization[];
    organizationsCount: number;

    // SD-9097: the orgs this rep holds as their BDR. A separate pair from the two above, because
    // the assignments are separate columns server-side — a BDR's orgs never appear in
    // `organizations`/`organizationsCount`, which is why the delete guard could not see them.
    bdrOrganizations: SimpleOrganization[];
    bdrOrganizationsCount: number;

    constructor(data: any) {
        super(data);

        if (data.id) {
            this.id = data.id;
        }

        if (data.userId) {
            this.userId = data.userId;
        }

        const timezone = LegFiJwtService.getTimezone();
        this.email = data.email ?? data.user?.email ?? 'onboarding@payhoa.com';

        // avatar already sets name, unless it's null
        if (data.user && data.user.memberships) {
            const membership = data.user.memberships[0];
            this.name = membership.profile.givenNames + (membership.profile.familyName ? ' ' + membership.profile.familyName : '');
        }
        this.name = this.name ?? 'Support';

        this.meetingLink = data.meetingLink ?? 'https://meetings.hubspot.com/payhoa/45-minute-launchpad-demo';
        this.secondaryMeetingLink = data.secondaryMeetingLink ?? 'https://meetings.hubspot.com/payhoa/30-minute-launchpad-round-robin';

        if (data.phone) {
            this.phone = data.phone;
        }

        if (data.lastLead) {
            this.lastLead = moment.utc(data.lastLead, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        // SD-9097: store() and update() both return the raw Eloquent model (snake_case), while
        // index() and show() hand-build camelCase arrays — so these flags arrive under either
        // spelling depending on which call produced the response. Read both, or they are
        // undefined immediately after a create or a save.
        if (data.hasOwnProperty('excludeFromLeads') || data.hasOwnProperty('exclude_from_leads')) {
            this.excludeFromLeads = data.excludeFromLeads ?? data.exclude_from_leads;
        }

        if (data.hasOwnProperty('isBdr') || data.hasOwnProperty('is_bdr')) {
            this.isBdr = data.isBdr ?? data.is_bdr;
        }

        if (data.weight) {
            this.weight = 1 / data.weight;
        }

        if (data.skipStart) {
            this.skipStart = moment.utc(data.skipStart, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.skipEnd) {
            this.skipEnd = moment.utc(data.skipEnd, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.lastSkipped) {
            this.lastSkipped = moment.utc(data.lastSkipped, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }

        if (data.organizations) {
            this.organizations = data.organizations.map((org: Object) => new SimpleOrganization(org));
        }

        if (data.organizationsCount) {
            this.organizationsCount = data.organizationsCount;
        }

        if (data.bdrOrganizations) {
            this.bdrOrganizations = data.bdrOrganizations.map((org: Object) => new SimpleOrganization(org));
        }

        if (data.bdrOrganizationsCount) {
            this.bdrOrganizationsCount = data.bdrOrganizationsCount;
        }
    }

    get description() {
        return this.name !== 'Support' ? 'Dedicated Onboarding Specialist' : 'PayHOA Onboarding Team';
    }
}
